import React, { useState, useEffect, useRef } from 'react';
import jsQR from 'jsqr';
import QRCode from 'qrcode';
import { useServer } from '../contexts/ServerContext';

function LocalMode() {
  const { isDebugMode } = useServer();
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('请点击 "开始扫描" 授权摄像头。');
  const [debugInfo, setDebugInfo] = useState('');
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);
  const [currentCodeInfo, setCurrentCodeInfo] = useState(null);
  const [localQrCode, setLocalQrCode] = useState('');
  const [localQrCodeImage, setLocalQrCodeImage] = useState('');
  const [currentMilliseconds, setCurrentMilliseconds] = useState('000');
  const [lastScannedCode, setLastScannedCode] = useState('');
  const [shouldStartScanning, setShouldStartScanning] = useState(false);
  const [lastProcessTime, setLastProcessTime] = useState(0);
  const SCAN_INTERVAL = 200; // 每200ms扫描一次
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scanFrameRef = useRef(null);
  const streamRef = useRef(null);
  const isScanningRef = useRef(false);

  // 检查是否是特定格式的码
  const isCheckworkFormat = (code) => {
    return /^checkwork\|id=\d+&siteId=\d+&createTime=\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}&classLessonId=\d+$/.test(code);
  };

  // 从码中提取关键信息
  const extractCodeInfo = (code) => {
    if (!isCheckworkFormat(code)) return null;
    const match = code.match(/id=(\d+)&siteId=(\d+)&createTime=.*?\.(\d{3})&classLessonId=(\d+)/);
    if (!match) return null;
    return {
      id: match[1],
      siteId: match[2],
      milliseconds: match[3],
      classLessonId: match[4]
    };
  };

  // 生成本地动态码
  const generateLocalCode = () => {
    if (!currentCodeInfo) return null;
    
    const now = new Date();
    const currentSeconds = now.getSeconds();
    const adjustedSeconds = Math.floor(currentSeconds / 5) * 5;
    const formattedSeconds = adjustedSeconds.toString().padStart(2, '0');
    
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    
    const timeStr = `${year}-${month}-${day}T${hours}:${minutes}:${formattedSeconds}.${currentMilliseconds}`;
    
    const newCode = `checkwork|id=${currentCodeInfo.id}&siteId=${currentCodeInfo.siteId}&createTime=${timeStr}&classLessonId=${currentCodeInfo.classLessonId}`;
    
    setDebugInfo(prev => prev + `\n[更新] 生成本地码，新时间: ${timeStr}`);
    return newCode;
  };

  // 获取可用的摄像头列表
  useEffect(() => {
    const getCameras = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: {
            facingMode: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) 
              ? { ideal: 'environment' }
              : 'user'
          } 
        });
        
        stream.getTracks().forEach(track => track.stop());
        
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setCameras(videoDevices);
        
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        
        if (isMobile) {
          const backCamera = videoDevices.find(device => 
            device.label.toLowerCase().includes('back') || 
            device.label.toLowerCase().includes('后置')
          );
          setSelectedCamera(backCamera ? backCamera.deviceId : videoDevices[0].deviceId);
        } else if (videoDevices.length > 0) {
          setSelectedCamera(videoDevices[0].deviceId);
        }
        
        setDebugInfo(prev => prev + '\n[成功] 获取到摄像头列表: ' + videoDevices.length + ' 个设备');
      } catch (err) {
        console.error('获取摄像头列表失败:', err);
        setDebugInfo(prev => prev + '\n[错误] 获取摄像头列表失败: ' + err.message);
        setError('无法访问摄像头，请确保：\n1. 已授予摄像头权限\n2. 使用 HTTPS 或 localhost\n3. 设备有可用的摄像头');
        
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        setSelectedCamera('default');
        setDebugInfo(prev => prev + '\n[降级] 使用默认摄像头配置');
      }
    };
    getCameras();
  }, []);

  // 处理本地动态码更新
  useEffect(() => {
    if (!currentCodeInfo) return;

    const updateLocalCode = async () => {
      try {
        const newCode = generateLocalCode();
        if (!newCode) {
          throw new Error('生成的码为空');
        }
        
        // 只在码真正改变时才更新状态
        if (newCode !== localQrCode) {
          setLocalQrCode(newCode);
          
          // 使用防抖处理二维码图片生成
          const generateQRImage = async () => {
            try {
              const qrImageUrl = await QRCode.toDataURL(newCode, {
                width: 300,
                margin: 2,
                color: {
                  dark: '#000000',
                  light: '#ffffff'
                }
              });
              setLocalQrCodeImage(qrImageUrl);
              setDebugInfo(prev => prev + `\n[更新] 本地码已更新: ${newCode}`);
            } catch (err) {
              setDebugInfo(prev => prev + '\n[错误] 生成本地二维码图片失败: ' + err.message);
            }
          };
          
          // 延迟100ms生成二维码图片，避免频繁更新
          setTimeout(generateQRImage, 100);
        }
      } catch (err) {
        setDebugInfo(prev => prev + '\n[错误] 生成本地二维码失败: ' + err.message);
      }
    };

    // 计算到下一个5秒整数时刻的延迟
    const now = new Date();
    const currentSeconds = now.getSeconds();
    const currentMilliseconds = now.getMilliseconds();
    const nextFiveSeconds = Math.ceil(currentSeconds / 5) * 5;
    const delay = (nextFiveSeconds - currentSeconds) * 1000 - currentMilliseconds;

    updateLocalCode();
    
    const initialTimeout = setTimeout(() => {
      updateLocalCode();
      const interval = setInterval(updateLocalCode, 5000);
      return () => {
        clearInterval(interval);
        clearTimeout(initialTimeout);
      };
    }, delay);

    return () => {
      clearTimeout(initialTimeout);
    };
  }, [currentCodeInfo, currentMilliseconds]);

  // 扫描二维码
  const scanQRCode = () => {
    if (!isScanningRef.current || !videoRef.current || !canvasRef.current || !streamRef.current) {
      return;
    }

    const now = Date.now();
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d', { willReadFrequently: true });

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      try {
        // 控制扫描频率
        if (now - lastProcessTime >= SCAN_INTERVAL) {
          setLastProcessTime(now);
          
          // 只在需要时更新canvas尺寸
          if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
          }
          
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
          
          const code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: "dontInvert",
            minSize: 100,
            maxSize: 500,
          });

          if (code && code.data !== lastScannedCode) {
            setLastScannedCode(code.data);
            
            // 检查是否是符合格式的码
            if (isCheckworkFormat(code.data)) {
              const codeInfo = extractCodeInfo(code.data);
              if (codeInfo) {
                setCurrentCodeInfo(codeInfo);
                setCurrentMilliseconds(codeInfo.milliseconds);
                setDebugInfo(prev => prev + `\n[成功] 扫描到有效的动态码: ${code.data}`);
                stopScanning(); // 停止扫描
                return;
              }
            }
            
            setDebugInfo(prev => prev + `\n[扫描] 扫描到新的二维码，但不是目标格式: ${code.data}`);
          }
        }

        // 继续扫描
        if (isScanningRef.current) {
          scanFrameRef.current = requestAnimationFrame(scanQRCode);
        }
      } catch (error) {
        setDebugInfo(prev => prev + `\n[错误] 扫描过程出错: ${error.message}`);
      }
    } else {
      if (isScanningRef.current) {
        scanFrameRef.current = requestAnimationFrame(scanQRCode);
      }
    }
  };

  // 开始扫描
  const startScanning = async () => {
    try {
      setError('');
      setFeedback('正在请求摄像头权限...');
      setDebugInfo(prev => prev + '\n[开始] 初始化扫描...');

      if (!videoRef.current || !canvasRef.current) {
        throw new Error('视频或Canvas元素未找到');
      }

      const constraints = {
        video: {
          ...(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? {
            facingMode: { exact: 'environment' },
            width: { ideal: 720 },
            height: { ideal: 1280 },
            frameRate: { ideal: 30 }
          } : {
            deviceId: selectedCamera && selectedCamera !== 'default' ? { exact: selectedCamera } : undefined,
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 }
          })
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      const videoElement = videoRef.current;
      videoElement.srcObject = stream;

      await new Promise((resolve, reject) => {
        videoElement.onloadedmetadata = async () => {
          try {
            await videoElement.play();
            setFeedback('摄像头已激活，请对准动态二维码。');
            
            if (canvasRef.current) {
              const context = canvasRef.current.getContext('2d', { willReadFrequently: true });
              if (!context) {
                throw new Error('无法获取 canvas 上下文');
              }
            }
            
            isScanningRef.current = true;
            setIsScanning(true);
            
            setTimeout(() => {
              if (isScanningRef.current) {
                scanQRCode();
              }
            }, 100);
            
            resolve();
          } catch (err) {
            reject(new Error('视频播放失败: ' + err.message));
          }
        };

        videoElement.onerror = (error) => {
          reject(new Error('视频加载失败: ' + error.message));
        };

        setTimeout(() => {
          reject(new Error('视频加载超时'));
        }, 5000);
      });

    } catch (err) {
      console.error('启动扫描失败:', err);
      setError(err.message);
      setFeedback('启动扫描失败: ' + err.message);
      setDebugInfo(prev => prev + '\n[错误] 启动扫描失败: ' + err.message);
      stopScanning();
    }
  };

  // 停止扫描
  const stopScanning = () => {
    isScanningRef.current = false;
    setIsScanning(false);
    setFeedback(currentCodeInfo ? '已获取到有效的动态码' : '已停止扫描');
    setDebugInfo(prev => prev + '\n[停止] 扫描已停止');
    
    if (scanFrameRef.current) {
      cancelAnimationFrame(scanFrameRef.current);
      scanFrameRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // 切换摄像头
  const handleCameraChange = async (event) => {
    setSelectedCamera(event.target.value);
    if (isScanning) {
      stopScanning();
      setShouldStartScanning(true);
    }
  };

  // 监听摄像头切换后的重新扫描
  useEffect(() => {
    if (shouldStartScanning) {
      startScanning();
      setShouldStartScanning(false);
    }
  }, [shouldStartScanning]);

  // 重新开始扫描
  const handleRescan = () => {
    stopScanning();
    setCurrentCodeInfo(null);
    setLocalQrCode('');
    setLocalQrCodeImage('');
    setCurrentMilliseconds('000');
    setLastScannedCode('');
    setShouldStartScanning(true);
  };

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  return (
    <div className="container mx-auto px-4 py-16">
      <h1 className="text-4xl font-bold text-center mb-12">本地模式</h1>
      
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <div className="space-y-6">
          {/* 摄像头选择 - 仅在非移动设备上显示且未扫描到有效码时 */}
          {!currentCodeInfo && cameras.length > 0 && !/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                选择摄像头
              </label>
              <select
                value={selectedCamera}
                onChange={handleCameraChange}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {cameras.map((camera) => (
                  <option key={camera.deviceId} value={camera.deviceId}>
                    {camera.label || `摄像头 ${camera.deviceId.slice(0, 5)}...`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* 视频预览区域 - 只在未扫描到有效码时显示 */}
          {!currentCodeInfo && (
            <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ 
                  transform: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) 
                    ? 'none'
                    : 'scaleX(-1)',
                  willChange: 'transform',
                  backfaceVisibility: 'hidden'
                }}
              />
              <canvas 
                ref={canvasRef} 
                className="hidden" 
                style={{
                  imageRendering: 'optimizeSpeed',
                  willChange: 'contents'
                }}
              />
            </div>
          )}

          {/* 控制按钮 */}
          <div className="flex space-x-4">
            {!currentCodeInfo ? (
              !isScanning ? (
                <button
                  onClick={() => setShouldStartScanning(true)}
                  className="flex-1 bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  开始扫描
                </button>
              ) : (
                <button
                  onClick={stopScanning}
                  className="flex-1 bg-red-500 text-white py-3 rounded-lg hover:bg-red-600 transition-colors"
                >
                  停止扫描
                </button>
              )
            ) : (
              <button
                onClick={handleRescan}
                className="flex-1 bg-yellow-500 text-white py-3 rounded-lg hover:bg-yellow-600 transition-colors"
              >
                重新扫描
              </button>
            )}
          </div>

          {/* 错误信息 */}
          {error && (
            <div className="p-4 bg-red-100 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {/* 反馈信息 */}
          <div className="text-center text-gray-600">
            {feedback}
          </div>

          {/* 本地动态码显示 */}
          {currentCodeInfo && localQrCode && (
            <div className="mt-8 text-center">
              <h2 className="text-xl font-semibold mb-4">本地动态码</h2>
              <div className="bg-blue-100 p-4 rounded-lg">
                {localQrCodeImage ? (
                  <div className="flex flex-col items-center">
                    <img 
                      src={localQrCodeImage} 
                      alt="Local QR Code" 
                      className="w-64 h-64 object-contain"
                    />
                    {isDebugMode && (
                      <p className="mt-4 font-mono text-sm break-all">{localQrCode}</p>
                    )}
                    <p className="mt-2 text-sm text-blue-600">
                      每5秒自动更新
                    </p>
                  </div>
                ) : (
                  <p className="font-mono text-2xl break-all">{localQrCode}</p>
                )}
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p>本地动态码会在每个5秒的整数时刻自动更新时间戳。</p>
              </div>
            </div>
          )}

          {/* 调试信息面板 */}
          {isDebugMode && (
            <div className="mt-4">
              <button
                onClick={() => setIsDebugPanelOpen(!isDebugPanelOpen)}
                className="w-full flex items-center justify-between p-4 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <h2 className="text-xl font-semibold">调试信息</h2>
                <span className="transform transition-transform duration-200" style={{ transform: isDebugPanelOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>
              {isDebugPanelOpen && (
                <div className="mt-2 p-4 bg-gray-100 rounded-lg">
                  <pre className="whitespace-pre-wrap text-sm max-h-60 overflow-y-auto">
                    {debugInfo}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LocalMode; 